import { TextField } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import isDecimal from 'validator/es/lib/isDecimal';
import isInt from 'validator/es/lib/isInt';

import useDynamicImport from '@/hooks/useDynamicImport.js';
import { useScrollToQuestion } from '@/hooks/useScrollToQuestion.js';

import { useStyles as useAnamnesisStyles } from '@/pages/HealthProfileAnamnesis/YourBodyComponents/YourBodyStyles.js';

import { useCustomAnamnesisStore } from '@/store.js';

import { useCustomAnamnesisStyles } from '../customAnamnesisStyles.js';
import { useNumberFieldQuestionStyles } from './QuestionTypesStyles.js';

// TODO move it to constants file when it will be merged
export const NumberValidation = {
    WHOLE_NUMBER: 'Whole Number',
    DECIMAL_NUMBER: 'Decimal Number',
};

const NumberfieldQuestion = ({
    question,
    answer,
    setAnswer,
    isSubQuestion = false,
    hideTitle = false,
    customValidation = () => true,
    required = false,
    requiredErrorMessage = undefined,
    customValidationErrorMessage = undefined,
}) => {
    const { t } = useTranslation();
    const anamnesisClasses = useAnamnesisStyles();
    const classes = useNumberFieldQuestionStyles();
    const [isValidAnswer, setIsValidAnswer] = useState(false);
    const [showRequiredError, setShowRequiredError] = useState(false);
    const [showCustomValidationError, setShowCustomValidationError] = useState(false);
    const customAnamnesisStyles = useCustomAnamnesisStyles();
    const highlightedQuestions = useCustomAnamnesisStore((state) => state.highlightedQuestions);
    const questionRef = useScrollToQuestion(question);
    const Joi = useDynamicImport(() => import('joi'));
    const schema = useMemo(() => Joi?.number(), [Joi]);

    useEffect(() => {
        if ((!isSubQuestion && !answer) || (isSubQuestion && !answer?.subQuestions[question.question_key[0].text])) {
            setShowCustomValidationError(false);
            return;
        }
        switch (question.number_field_validation) {
            case NumberValidation.WHOLE_NUMBER:
                setIsValidAnswer(isInt(!isSubQuestion ? answer : answer?.subQuestions[question.question_key[0].text]));
                break;
            case NumberValidation.DECIMAL_NUMBER:
            default:
                setIsValidAnswer(
                    isDecimal(!isSubQuestion ? answer : answer?.subQuestions[question.question_key[0].text])
                );
                break;
        }

        // Check custom validation
        const currentAnswer = !isSubQuestion ? answer : answer?.subQuestions[question.question_key[0].text];
        if (currentAnswer && !customValidation(currentAnswer)) {
            setShowCustomValidationError(true);
        } else {
            setShowCustomValidationError(false);
        }
    }, [answer, isSubQuestion, customValidation, question.question_key]);

    const handleChange = async (e) => {
        if (!schema || (e.target.value && schema.validate(e.target.value).error)) return;

        if (!isSubQuestion) {
            setAnswer(e.target.value);
        } else {
            setAnswer({
                ...answer,
                subQuestions: {
                    ...answer.subQuestions,
                    [question.question_key[0].text]: e.target.value,
                },
            });
        }

        if (e.target.value && highlightedQuestions[question.question_key[0].text]) {
            useCustomAnamnesisStore.setState({
                highlightedQuestions: {
                    ...highlightedQuestions,
                    [question.question_key[0].text]: false,
                },
            });
        }

        // Clear required error when user enters a value
        if (e.target.value && showRequiredError) {
            setShowRequiredError(false);
        }
    };

    const onBlur = () => {
        const currentAnswer = !isSubQuestion ? answer : answer?.subQuestions[question.question_key[0].text];
        if (required && !currentAnswer) {
            setShowRequiredError(true);
        }
    };

    return (
        <div
            ref={questionRef}
            data-testid={`question-${question.question_key[0].text}`}
            className={clsx(
                customAnamnesisStyles.defaultQuestion,
                required &&
                    !highlightedQuestions[question.question_key[0].text] &&
                    customAnamnesisStyles.requiredIndicator,
                highlightedQuestions[question.question_key[0].text] && customAnamnesisStyles.highlight
            )}
        >
            {!isSubQuestion && !hideTitle && (
                <Typography style={{ marginBottom: '1rem' }} className={classes.title}>
                    {question.title[0].text}
                </Typography>
            )}
            <TextField
                label={!isSubQuestion ? question.label[0]?.text : question.title[0].text}
                variant="filled"
                autoComplete="off"
                color="primary"
                required={required}
                value={(!isSubQuestion ? answer : answer?.subQuestions[question.question_key[0].text]) ?? ''}
                onChange={handleChange}
                onBlur={onBlur}
                classes={{
                    root: clsx(
                        classes.textfield,
                        (!isSubQuestion ? answer : answer?.subQuestions[question.question_key[0].text]) &&
                            (!isValidAnswer ||
                                !customValidation(
                                    !isSubQuestion ? answer : answer?.subQuestions[question.question_key[0].text]
                                )) &&
                            classes.error,
                        showRequiredError && classes.error,
                        required && classes.requiredLabel
                    ),
                }}
                style={{ width: '100%' }}
                InputLabelProps={{
                    classes: {
                        root: anamnesisClasses.inputLabel,
                        focused: 'focused',
                    },
                }}
                InputProps={{
                    classes: {
                        root: anamnesisClasses.filledInputRoot,
                        focused: anamnesisClasses.fieldFocused,
                    },
                }}
            />
            {showCustomValidationError && customValidationErrorMessage && (
                <Typography className={classes.errorText}>{customValidationErrorMessage}</Typography>
            )}
            {showRequiredError && (
                <Typography className={classes.errorText}>{requiredErrorMessage || t('RequiredField')}</Typography>
            )}
        </div>
    );
};

export default NumberfieldQuestion;
